import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LayoutGrid, Filter, CalendarDays, Zap, ChevronLeft, ChevronRight, ArrowDownUp, ChevronDown, Check, SlidersHorizontal, X, Search } from 'lucide-react';
// Tùy đường dẫn của bạn
import ProductCard from './ProductCard'; 
import { PRODUCT_STATUS } from '../../utils/constants';

const stripHtml = (html) => {
    if (!html) return '';
    return String(html)
        .replace(/<\/(p|div|li|h[1-6])>/gi, ' ') 
        .replace(/<br\s*\/?>/gi, ' ') 
        .replace(/<[^>]*>?/gm, '') 
        .replace(/&nbsp;/g, ' ') 
        .replace(/\s+/g, ' ') 
        .trim();
};

const formatNumberWithDots = (numStr) => {
    if (!numStr) return '';
    const cleanStr = String(numStr).replace(/\D/g, ''); 
    return cleanStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const SaleGroupSection = ({ sale = {}, products = [], adminSpecs = [], getImageUrl }) => {

    const sectionRef = useRef(null);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    
    const [sortOrder, setSortOrder] = useState('default');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [inStockOnly, setInStockOnly] = useState(false);

    const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
    const [openSpecDropdown, setOpenSpecDropdown] = useState(null);
    const [openSortDropdown, setOpenSortDropdown] = useState(false); 
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; 

    const sortOptions = [
        { value: 'default', label: 'Sắp xếp: Mặc định' },
        { value: 'price_asc', label: 'Giá: Thấp đến Cao' },
        { value: 'price_desc', label: 'Giá: Cao đến Thấp' }
    ];

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, selectedBrands, sortOrder, minPrice, maxPrice, inStockOnly, selectedFilters]);

    const memoizedProducts = useMemo(() => {
        const validProducts = Array.isArray(products) ? products : [];
        return validProducts.map(p => {
            let activePrice = -1;
            if (p?.itemReleasePrice && p.itemReleasePrice > 0) {
                const isFlashSale = p?.itemFlashSalePrice && p.itemFlashSalePrice > 0 && p?.itemFlashSaleQuantity > 0;
                activePrice = isFlashSale ? p.itemFlashSalePrice : (p.itemSalePrice || p.itemReleasePrice);
            }

            const cleanSpecsMap = {};
            if (p?.itemSpecs && Array.isArray(p.itemSpecs)) {
                p.itemSpecs.forEach(spec => {
                    const specName = spec?.specName?.trim();
                    if (specName) {
                        cleanSpecsMap[specName] = stripHtml(spec?.specValue);
                    }
                });
            }

            return {
                ...p,
                _cachedPrice: activePrice,
                _cleanSpecsMap: cleanSpecsMap,
                _brandId: String(p?.brand?.brandId || p?.brandId),
                _categoryId: String(p?.categoryId || p?.deviceCategory?.deviceCategoryId)
            };
        });
    }, [products]);

    const { availableBrands, availableCategories } = useMemo(() => {
        const bMap = new Map();
        const cMap = new Map();
        
        memoizedProducts.forEach(p => {
            const bId = p._brandId;
            const bName = p?.brand?.brandName;
            const bImg = p?.brand?.brandImageUrl;
            if (bId && bId !== "undefined" && bName) bMap.set(bId, { name: bName, img: bImg });

            const cId = p._categoryId;
            const cName = p?.deviceCategory?.deviceCategoryName;
            if (cId && cId !== "undefined" && cName) cMap.set(cId, cName);
        });

        return {
            availableBrands: Array.from(bMap.entries()).map(([id, data]) => ({ id, ...data })),
            availableCategories: Array.from(cMap.entries()).map(([id, name]) => ({ id, name }))
        };
    }, [memoizedProducts]);

    const availableSpecs = useMemo(() => {
        const validAdminSpecs = Array.isArray(adminSpecs) ? adminSpecs : [];
        if (validAdminSpecs.length === 0 || memoizedProducts.length === 0) return [];
        
        const uniqueCategoryIds = new Set();
        memoizedProducts.forEach(p => {
            if (p._categoryId && p._categoryId !== "undefined") uniqueCategoryIds.add(p._categoryId);
        });

        if (uniqueCategoryIds.size !== 1) return [];

        const targetCategoryId = Array.from(uniqueCategoryIds)[0];

        const relevantAdminSpecs = validAdminSpecs.filter(s => {
            const adminCatId = s?.categoryId || s?.deviceCategory?.deviceCategoryId;
            return String(adminCatId) === targetCategoryId && s?.isFilterable === true;
        });

        const activeValuesMap = {};
        memoizedProducts.forEach(p => {
            Object.entries(p._cleanSpecsMap).forEach(([specName, cleanValue]) => {
                if (!activeValuesMap[specName]) activeValuesMap[specName] = new Set();
                if (cleanValue) activeValuesMap[specName].add(cleanValue);
            });
        });

        const formattedSpecs = [];
        relevantAdminSpecs.forEach(adminSpec => {
            const specName = adminSpec?.specName?.trim();
            if (specName && activeValuesMap[specName] && activeValuesMap[specName].size > 0) {
                formattedSpecs.push({
                    name: specName,
                    values: Array.from(activeValuesMap[specName]).sort()
                });
            }
        });

        return formattedSpecs;
    }, [memoizedProducts, adminSpecs]);

    const filteredAndSortedProducts = useMemo(() => {
        let result = [...memoizedProducts]; 

        result = result.filter(p => 
            p?.itemStatus !== PRODUCT_STATUS.HIDDEN && 
            p?.itemStatus !== PRODUCT_STATUS.DISCONTINUED
        );

        if (selectedCategory !== 'all') {
            result = result.filter(p => p._categoryId === selectedCategory);
        }

        if (selectedBrands.length > 0) {
            result = result.filter(p => selectedBrands.includes(p._brandId));
        }

        if (selectedFilters && Object.keys(selectedFilters).length > 0) {
            result = result.filter(p => {
                for (const [specName, allowedValues] of Object.entries(selectedFilters)) {
                    const productValue = p._cleanSpecsMap[specName.trim()];
                    if (!productValue || !allowedValues.includes(productValue)) {
                        return false;
                    }
                }
                return true;
            });
        }

        if (inStockOnly) {
            result = result.filter(p => p?.itemQuantity > 0 && p?.itemStatus === PRODUCT_STATUS.IN_STOCK);
        }

        const currentMin = minPrice !== '' ? parseInt(minPrice.replace(/\./g, '')) : 0;
        const currentMax = maxPrice !== '' ? parseInt(maxPrice.replace(/\./g, '')) : Infinity;
        
        if (currentMin > 0 || currentMax !== Infinity) {
            result = result.filter(p => {
                const price = p._cachedPrice;
                if (price < 0) return false; 
                return price >= currentMin && price <= currentMax;
            });
        }

        if (sortOrder === 'price_asc' || sortOrder === 'price_desc') {
            result.sort((a, b) => {
                const priceA = a._cachedPrice;
                const priceB = b._cachedPrice;
                if (priceA < 0 && priceB < 0) return 0;
                if (priceA < 0) return 1;
                if (priceB < 0) return -1;
                return sortOrder === 'price_asc' ? priceA - priceB : priceB - priceA;
            });
        } else {
            result.sort((a, b) => {
                const prioA = a?.priority || 0;
                const prioB = b?.priority || 0;
                if (prioA !== prioB) return prioB - prioA; 
                return (b?.itemId || 0) - (a?.itemId || 0); 
            });
        }
        return result;
    }, [memoizedProducts, selectedCategory, selectedBrands, sortOrder, minPrice, maxPrice, inStockOnly, selectedFilters]);

    const handleBrandChange = (brandId) => {
        setSelectedBrands(prev => prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]);
    };

    const handleFilterChange = (specName, specValue) => {
        setSelectedFilters(prev => {
            const currentValues = prev[specName] || [];
            if (currentValues.includes(specValue)) {
                const newValues = currentValues.filter(v => v !== specValue);
                if (newValues.length === 0) {
                    const newState = { ...prev };
                    delete newState[specName];
                    return newState;
                }
                return { ...prev, [specName]: newValues };
            } else {
                return { ...prev, [specName]: [...currentValues, specValue] };
            }
        });
    };

    const clearAllFilters = () => {
        setSelectedCategory('all');
        setSelectedBrands([]);
        setSelectedFilters({});
        setSortOrder('default');
        setInStockOnly(false);
        setMinPrice('');
        setMaxPrice('');
        setOpenSpecDropdown(null);
    };

    const handlePriceInput = (setter) => (e) => {
        const formatted = formatNumberWithDots(e.target.value);
        setter(formatted);
    };

    const handlePriceBlur = (val, setter) => () => {
        if (!val) return;
        let cleanNum = parseInt(val.replace(/\./g, ''), 10);
        setter(formatNumberWithDots(cleanNum.toString()));
    };

    const totalPages = Math.ceil(filteredAndSortedProducts.length / itemsPerPage) || 1;
    const paginatedProducts = filteredAndSortedProducts.slice(
        (currentPage - 1) * itemsPerPage, 
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            const yOffset = -100; 
            const element = sectionRef.current;
            if (element) {
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            return new Date(dateString).toLocaleDateString('vi-VN', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        } catch(e) { return ""; }
    };

    const getPaginationRange = () => {
        const delta = 2; 
        const range = [];
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            range.push(i);
        }
        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    };

    const activeFilterCount = (selectedCategory !== 'all' ? 1 : 0) + selectedBrands.length + Object.keys(selectedFilters).length + (minPrice ? 1 : 0) + (maxPrice ? 1 : 0) + (inStockOnly ? 1 : 0) + (sortOrder !== 'default' ? 1 : 0);

    if (!sale || Object.keys(sale).length === 0) return null;

    return (
        <section ref={sectionRef} className="bg-gradient-to-r from-orange-100 to-white rounded-2xl shadow-sm border border-orange-300/90 overflow-hidden mb-8 [content-visibility:auto]">
            <div className="p-5 border-b border-orange-300/90">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-tnc-orange text-white p-2 rounded-lg shadow-sm">
                            <Zap size={24} className="fill-current" />
                        </div>
                        <h2 className="text-xl md:text-2xl font-black text-tnc-orange tracking-tight uppercase drop-shadow-sm">
                            {sale?.saleName || 'Chương trình Khuyến mãi'}
                        </h2>
                    </div>
                    
                    {sale?.saleStartDate && sale?.saleEndDate && (
                        <div className="flex items-center gap-2 text-xs md:text-sm font-bold text-gray-500 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm shrink-0">
                            <CalendarDays size={16} className="text-tnc-orange" />
                            <span>Từ {formatDate(sale.saleStartDate)}</span>
                            <span className="text-gray-300">-</span>
                            <span>Đến {formatDate(sale.saleEndDate)}</span>
                        </div>
                    )}
                </div>

                {/* 👉 TỐI ƯU 1: Bọc CSS ngăn thẻ IMG tải từ CKEditor vượt quá container gây vỡ layout */}
                {sale?.description && (
                    <div 
                        className="text-[13.5px] md:text-sm text-gray-700 leading-relaxed bg-white/60 p-4 rounded-xl border border-white/50 backdrop-blur-sm
                        [&_*]:!whitespace-normal [&_*]:![word-break:normal] [&_*]:![overflow-wrap:anywhere]
                        [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1
                        [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1
                        [&_li]:my-1 [&_p]:my-1 [&_strong]:font-extrabold [&_strong]:text-gray-900
                        [&_img]:max-w-full [&_img]:h-auto [&_img]:aspect-video [&_img]:object-contain"
                        dangerouslySetInnerHTML={{ 
                            __html: String(sale.description).replace(/&nbsp;/g, ' ').replace(/<img /g, '<img loading="lazy" decoding="async" ')
                        }}
                    />
                )}

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-5 mt-5 border-t border-orange-200/60">
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all shadow-sm border ${
                                isFilterOpen 
                                ? 'bg-tnc-orange text-white border-tnc-orange hover:bg-tnc-orange-hover' 
                                : 'bg-white text-tnc-orange border-orange-200 hover:bg-orange-50'
                            }`}
                        >
                            <Filter size={18} /> 
                            {isFilterOpen ? 'Thu gọn bộ lọc' : 'Lọc sản phẩm'}
                            {activeFilterCount > 0 && !isFilterOpen && (
                                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full ml-1 animate-pulse">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-bold border border-gray-200 shadow-sm">
                            {filteredAndSortedProducts.length} SP
                        </span>
                    </div>

                    {(activeFilterCount > 0) && (
                        <button onClick={clearAllFilters} className="text-sm text-tnc-orange font-bold hover:underline flex items-center gap-1">
                            <X size={16}/> Xóa tất cả bộ lọc
                        </button>
                    )}
                </div>

                {isFilterOpen && (
                    <div className="mt-5 space-y-5 animate-in fade-in slide-in-from-top-4 duration-300 min-h-[60px]">
                        <div className="flex flex-wrap items-center gap-3 text-sm bg-gradient-to-r from-orange-50 to-white p-3 rounded-xl border border-orange-200/60">
                            
                            {availableCategories.length > 0 && (
                                <div className="relative">
                                    <button 
                                        onClick={() => setOpenCategoryDropdown(!openCategoryDropdown)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold transition-colors shadow-sm ${
                                            selectedCategory !== 'all' 
                                            ? 'border-tnc-orange text-tnc-orange bg-white' 
                                            : 'border-orange-200 text-gray-700 bg-white hover:border-tnc-orange'
                                        }`}
                                    >
                                        <LayoutGrid size={14} className={selectedCategory !== 'all' ? "text-tnc-orange" : "text-gray-500"} />
                                        <span className="truncate max-w-[160px]">
                                            {selectedCategory === 'all' 
                                                ? 'Tất cả loại sản phẩm' 
                                                : availableCategories.find(c => String(c.id) === selectedCategory)?.name || 'Tất cả loại sản phẩm'}
                                        </span>
                                        <ChevronDown size={14} className={`transition-transform ${openCategoryDropdown ? 'rotate-180' : ''}`}/>
                                    </button>

                                    {openCategoryDropdown && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setOpenCategoryDropdown(false)}></div>
                                            <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1 max-h-60 overflow-y-auto custom-scrollbar">
                                                <button 
                                                    onClick={() => {
                                                        setSelectedCategory('all');
                                                        setOpenCategoryDropdown(false);
                                                    }}
                                                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                                                        selectedCategory === 'all' 
                                                        ? 'bg-orange-50 font-bold text-tnc-orange' 
                                                        : 'text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span>Tất cả loại sản phẩm</span>
                                                    {selectedCategory === 'all' && <Check size={16} className="text-tnc-orange" />}
                                                </button>
                                                {availableCategories.map(cat => {
                                                    const isChecked = selectedCategory === String(cat.id);
                                                    return (
                                                        <button 
                                                            key={cat.id}
                                                            onClick={() => {
                                                                setSelectedCategory(String(cat.id));
                                                                setOpenCategoryDropdown(false);
                                                            }}
                                                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                                                                isChecked 
                                                                ? 'bg-orange-50 font-bold text-tnc-orange' 
                                                                : 'text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <span className="line-clamp-2 pr-2">{cat.name}</span>
                                                            {isChecked && <Check size={16} className="text-tnc-orange shrink-0" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="relative">
                                <button 
                                    onClick={() => setOpenSortDropdown(!openSortDropdown)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold transition-colors shadow-sm ${
                                        sortOrder !== 'default' 
                                        ? 'border-tnc-orange text-tnc-orange bg-white' 
                                        : 'border-orange-200 text-gray-700 bg-white hover:border-tnc-orange'
                                    }`}
                                >
                                    <ArrowDownUp size={14} className={sortOrder !== 'default' ? "text-tnc-orange" : "text-gray-500"} />
                                    {sortOptions.find(o => o.value === sortOrder)?.label || 'Sắp xếp'}
                                    <ChevronDown size={14} className={`transition-transform ${openSortDropdown ? 'rotate-180' : ''}`}/>
                                </button>

                                {openSortDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setOpenSortDropdown(false)}></div>
                                        <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1">
                                            {sortOptions.map(option => {
                                                const isChecked = sortOrder === option.value;
                                                return (
                                                    <button 
                                                        key={option.value}
                                                        onClick={() => {
                                                            setSortOrder(option.value);
                                                            setOpenSortDropdown(false);
                                                        }}
                                                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                                                            isChecked 
                                                            ? 'bg-orange-50 font-bold text-tnc-orange' 
                                                            : 'text-gray-600 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <span>{option.label}</span>
                                                        {isChecked && <Check size={16} className="text-tnc-orange" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="flex items-center border border-orange-200 rounded-lg bg-white overflow-hidden focus-within:border-tnc-orange transition-colors shadow-sm hover:border-tnc-orange">
                                    <input 
                                        type="text" 
                                        placeholder="Giá thấp nhất..." 
                                        value={minPrice}
                                        onChange={handlePriceInput(setMinPrice)}
                                        onBlur={handlePriceBlur(minPrice, setMinPrice)}
                                        className="w-28 sm:w-32 px-3 py-2 bg-transparent outline-none text-gray-700 font-bold text-sm"
                                    />
                                </div>
                                <span className="text-orange-300 font-bold">-</span>
                                <div className="flex items-center border border-orange-200 rounded-lg bg-white overflow-hidden focus-within:border-tnc-orange transition-colors shadow-sm hover:border-tnc-orange">
                                    <input 
                                        type="text" 
                                        placeholder="Giá cao nhất..." 
                                        value={maxPrice}
                                        onChange={handlePriceInput(setMaxPrice)}
                                        onBlur={handlePriceBlur(maxPrice, setMaxPrice)}
                                        className="w-28 sm:w-32 px-3 py-2 bg-transparent outline-none text-gray-700 font-bold text-sm"
                                    />
                                </div>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer border border-orange-200 bg-white shadow-sm rounded-lg px-3 py-2 hover:border-tnc-orange transition-colors ml-auto sm:ml-0">
                                <input 
                                    type="checkbox" 
                                    checked={inStockOnly} 
                                    onChange={(e) => setInStockOnly(e.target.checked)} 
                                    className="w-4 h-4 accent-tnc-orange cursor-pointer"
                                />
                                <span className="font-bold text-gray-700 text-sm">Chỉ Sẵn hàng</span>
                            </label>
                        </div>

                        {availableBrands.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="text-sm font-bold text-gray-500 mr-2 uppercase tracking-wide">Hãng sx:</span>
                                {availableBrands.map(brand => {
                                    const isSelected = selectedBrands.includes(String(brand.id));
                                    return (
                                        <button
                                            key={brand.id}
                                            onClick={() => handleBrandChange(String(brand.id))}
                                            className={`h-12 px-4 bg-white border-2 rounded-xl flex items-center justify-center transition-all ${
                                                isSelected 
                                                    ? 'border-tnc-orange shadow-md bg-orange-50 scale-105' 
                                                    : 'border-gray-200 hover:border-orange-300 hover:shadow-sm'
                                            }`}
                                            title={brand.name}
                                        >
                                            {brand.img ? (
                                                <img src={getImageUrl(brand.img)} alt={brand.name} className="h-8 w-auto max-w-[80px] object-contain" />
                                            ) : (
                                                <span className={`text-sm font-bold ${isSelected ? 'text-tnc-orange' : 'text-gray-600'}`}>{brand.name}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {availableSpecs.length > 0 && (
                            <div className="flex flex-wrap gap-2.5 items-center mt-1">
                                <div className="flex items-center gap-2 text-gray-500 mr-1">
                                    <SlidersHorizontal size={16} />
                                    <span className="font-bold text-sm uppercase tracking-wide">Thông số:</span>
                                </div>
                                
                                {availableSpecs.map((specGroup, idx) => {
                                    const selectedCount = selectedFilters[specGroup.name]?.length || 0;
                                    const isOpen = openSpecDropdown === specGroup.name;

                                    return (
                                        <div key={idx} className="relative">
                                            <button 
                                                onClick={() => setOpenSpecDropdown(isOpen ? null : specGroup.name)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-bold transition-colors shadow-sm ${selectedCount > 0 ? 'border-tnc-orange text-tnc-orange bg-orange-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50 bg-white hover:border-orange-300'}`}
                                            >
                                                {specGroup.name}: {selectedCount === 0 ? 'Tất cả' : `${selectedCount} đã chọn`}
                                                <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180 text-tnc-orange' : ''}`}/>
                                            </button>

                                            {isOpen && (
                                                <>
                                                    <div className="fixed inset-0 z-40" onClick={() => setOpenSpecDropdown(null)}></div>
                                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 flex flex-col gap-3">
                                                        <div className="max-h-60 overflow-y-auto pr-2 custom-scrollbar flex flex-col gap-2">
                                                            {specGroup.values.map((val, vIdx) => {
                                                                const isChecked = selectedFilters[specGroup.name]?.includes(val) || false;
                                                                return (
                                                                    <label key={vIdx} className="flex items-start gap-3 cursor-pointer group py-1.5 hover:bg-orange-50/50 px-2 rounded transition-colors">
                                                                        <input 
                                                                            type="checkbox" 
                                                                            checked={isChecked}
                                                                            onChange={() => handleFilterChange(specGroup.name, val)}
                                                                            className="mt-0.5 w-4 h-4 accent-tnc-orange cursor-pointer shrink-0"
                                                                        />
                                                                        <span className={`text-sm transition-colors ${isChecked ? 'font-bold text-tnc-orange' : 'text-gray-600 group-hover:text-gray-900 font-medium'}`}>
                                                                            {val}
                                                                        </span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                        <div className="border-t border-gray-100 pt-3 flex justify-between items-center">
                                                            {selectedCount > 0 ? (
                                                                <button 
                                                                    onClick={() => {
                                                                        const newState = { ...selectedFilters };
                                                                        delete newState[specGroup.name];
                                                                        setSelectedFilters(newState);
                                                                    }}
                                                                    className="text-xs text-gray-500 hover:text-tnc-orange-hover font-medium hover:underline"
                                                                >
                                                                    Bỏ chọn
                                                                </button>
                                                            ) : <div></div>}
                                                            <button 
                                                                onClick={() => setOpenSpecDropdown(null)}
                                                                className="text-xs font-bold bg-tnc-orange text-white px-4 py-2 rounded-lg hover:bg-tnc-orange-hover transition-colors shadow-sm"
                                                            >
                                                                Xong
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {(selectedCategory !== 'all' || selectedBrands.length > 0 || Object.keys(selectedFilters).length > 0) && (
                            <div className="flex flex-wrap gap-2 mt-2 p-3 bg-gray-50 rounded-xl border border-dashed border-gray-200 w-full min-h-[50px]">
                                <span className="text-xs font-bold text-gray-400 mt-2">Đang lọc:</span>
                                {selectedCategory !== 'all' && (() => {
                                    const c = availableCategories.find(cat => String(cat.id) === String(selectedCategory));
                                    if(!c) return null;
                                    return (
                                        <span className="flex items-center gap-1.5 bg-white border border-orange-200 text-tnc-orange px-3 py-1.5 rounded-full text-xs font-bold shadow-sm animate-in fade-in">
                                            Loại SP: {c.name}
                                            <X size={14} className="cursor-pointer hover:text-white hover:bg-red-500 bg-orange-100 rounded-full p-0.5 transition-colors" onClick={() => setSelectedCategory('all')}/>
                                        </span>
                                    );
                                })()}
                                
                                {selectedBrands.map(brandId => {
                                    const b = availableBrands.find(br => String(br.id) === String(brandId));
                                    if(!b) return null;
                                    return (
                                        <span key={`brand-${brandId}`} className="flex items-center gap-1.5 bg-white border border-orange-200 text-tnc-orange px-3 py-1.5 rounded-full text-xs font-bold shadow-sm animate-in fade-in">
                                            Hãng: {b.name}
                                            <X size={14} className="cursor-pointer hover:text-white hover:bg-red-500 bg-orange-100 rounded-full p-0.5 transition-colors" onClick={() => handleBrandChange(brandId)}/>
                                        </span>
                                    );
                                })}

                                {Object.entries(selectedFilters).map(([sName, sValues]) => 
                                    sValues.map(val => (
                                        <span key={`${sName}-${val}`} className="flex items-center gap-1.5 bg-white border border-orange-200 text-tnc-orange px-3 py-1.5 rounded-full text-xs font-bold shadow-sm animate-in fade-in">
                                            {sName}: {val}
                                            <X size={14} className="cursor-pointer hover:text-white hover:bg-red-500 bg-orange-100 rounded-full p-0.5 transition-colors" onClick={() => handleFilterChange(sName, val)}/>
                                        </span>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="p-4 md:p-6 bg-gray-50/50 border-t border-orange-200/60 min-h-[400px]">
                {paginatedProducts.length > 0 ? (
                    <>
                        {/* 👉 TỐI ƯU 2: Gỡ bỏ class "flex" dư thừa gây vỡ Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                            {paginatedProducts.map(product => (
                                <ProductCard 
                                    key={product.itemId} 
                                    product={product} 
                                    getImageUrl={getImageUrl} 
                                    adminSpecs={adminSpecs} 
                                />
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 border-t border-gray-200 pt-6">
                                <div className="text-sm text-gray-500 font-medium">
                                    Hiển thị <strong className="text-gray-900">{(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredAndSortedProducts.length)}</strong> trên tổng số <strong className="text-gray-900">{filteredAndSortedProducts.length}</strong> sản phẩm
                                </div>

                                <div className="flex flex-wrap items-center justify-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1}
                                            className="px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                        >
                                            <ChevronLeft size={16} /> <span className="hidden sm:inline">Trang trước</span>
                                        </button>

                                        <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] md:max-w-none scrollbar-hide">
                                            {getPaginationRange().map((page, index) => {
                                                if (page === "...") {
                                                    return <span key={`dots-${index}`} className="px-2 py-1 text-gray-400 font-bold">...</span>;
                                                }
                                                return (
                                                    <button 
                                                        key={index} 
                                                        onClick={() => handlePageChange(page)}
                                                        className={`w-10 h-10 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                            ${currentPage === page 
                                                                ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                            }`}
                                                    >
                                                        {page}
                                                    </button>
                                                )
                                            })}
                                        </div>

                                        <button 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === totalPages}
                                            className="px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                        >
                                            <span className="hidden sm:inline">Trang sau</span> <ChevronRight size={16} />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                        <span className="text-sm text-gray-500 font-medium">Đến trang:</span>
                                        <input 
                                            key={`sale-group-page-${currentPage}`}
                                            type="number" min={1} max={totalPages} defaultValue={currentPage}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const page = parseInt(e.target.value);
                                                    if (page >= 1 && page <= totalPages) handlePageChange(page);
                                                    else e.target.value = currentPage;
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const page = parseInt(e.target.value);
                                                if (page >= 1 && page <= totalPages && page !== currentPage) handlePageChange(page);
                                                else e.target.value = currentPage;
                                            }}
                                            className="w-16 h-10 px-2 text-center border border-gray-200 rounded-lg outline-none text-sm font-bold text-gray-700 focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm transition-all"
                                            title="Nhập số trang và nhấn Enter"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200 shadow-sm">
                        <Search className="mx-auto text-gray-300 w-12 h-12 mb-3" />
                        <h3 className="text-lg font-bold text-gray-700 mb-1">Không tìm thấy sản phẩm!</h3>
                        <p className="text-gray-500 font-medium mb-4">Vui lòng điều chỉnh lại bộ lọc để xem các sản phẩm khác.</p>
                        <button onClick={clearAllFilters} className="bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold py-2 px-6 rounded-lg transition-colors shadow-sm">
                            Xóa tất cả bộ lọc
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default SaleGroupSection;